import React, { useState, useEffect } from "react";
import { doc, setDoc, getDoc } from "firebase/firestore";
import { auth, db } from "./firebase";
import { useNavigate } from "react-router-dom";
import { countries } from "countries-list";

const SetupProfile = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  const [form, setForm] = useState({
    country: "",
    graduationTime: "",
    step1: "",
    step2: "",
    usce: "",
    visa: "",
    obtainedMarks: "",
    maximumMarks: ""
  });

  // Check if user has existing profile data
  useEffect(() => {
    const checkExistingProfile = async () => {
      const uid = auth.currentUser?.uid;
      if (!uid) {
        setIsLoading(false);
        return;
      }

      try {
        const docSnap = await getDoc(doc(db, "profiles", uid));
        if (docSnap.exists()) {
          const existingData = docSnap.data();
          setForm(existingData);
          setIsEditing(true);
        }
      } catch (error) {
        console.error("Error fetching existing profile:", error);
      } finally {
        setIsLoading(false);
      }
    };

    checkExistingProfile();
  }, []);

  const handleChange = (e) => {
    // Prevent changing USMLE Step 1 and Step 2 if editing existing profile
    if ((e.target.name === "step1" || e.target.name === "step2") && isEditing && form[e.target.name]) {
      return; // Don't allow changes to step1 or step2 when editing
    }

    // Prevent changing aggregate marks if editing existing profile and marks are already set
    if ((e.target.name === "obtainedMarks" || e.target.name === "maximumMarks") && isEditing && form.obtainedMarks && form.maximumMarks) {
      return; // Don't allow changes to aggregate marks when editing
    }

    setForm((prev) => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
    setMessage("");
  };

  const getCompletionPercentage = () => {
    const totalFields = Object.keys(form).length;
    const completedFields = Object.values(form).filter(value => value !== "").length;
    return Math.round((completedFields / totalFields) * 100);
  };

  // Calculate percentage for aggregate marks
  const calculatePercentage = () => {
    const obtained = parseFloat(form.obtainedMarks);
    const maximum = parseFloat(form.maximumMarks);
    
    if (obtained && maximum && maximum > 0) {
      return ((obtained / maximum) * 100).toFixed(2);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setMessage("");

    const uid = auth.currentUser?.uid;

    if (!uid) {
      setMessage("❌ User authentication required. Please log in again.");
      setIsSubmitting(false);
      return;
    }

    // Validate aggregate marks
    const obtained = parseFloat(form.obtainedMarks);
    const maximum = parseFloat(form.maximumMarks);
    
    if (obtained && maximum && obtained > maximum) {
      setMessage("❌ Obtained marks cannot be greater than maximum marks.");
      setIsSubmitting(false);
      return;
    }

    try {
      const profileData = {
        ...form,
        updatedAt: new Date().toISOString(),
        profileComplete: true
      };

      // Add calculated percentage if marks are provided
      const percentage = calculatePercentage();
      if (percentage !== null) {
        profileData.aggregatePercentage = parseFloat(percentage);
      }

      // Only add createdAt if it's a new profile
      if (!isEditing) {
        profileData.createdAt = new Date().toISOString();
      }

      await setDoc(doc(db, "profiles", uid), profileData, { merge: true });

      setMessage(isEditing ? "✅ Profile updated successfully! Redirecting..." : "✅ Profile completed successfully! Redirecting...");
      
      setTimeout(() => {
        navigate("/dashboard");
      }, 1500);
    } catch (err) {
      console.error("Error saving profile:", err);
      setMessage("❌ Failed to save profile. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // Generate score ranges in increments of 5
  const renderScoreRanges = (start, end) => {
    const ranges = [];
    for (let i = start; i < end; i += 5) {
      const rangeEnd = Math.min(i + 4, end);
      ranges.push(
        <option key={`${i}-${rangeEnd}`} value={`${i}-${rangeEnd}`}>
          {i}-{rangeEnd}
        </option>
      );
    }
    return ranges;
  };

  const completionPercentage = getCompletionPercentage();
  const aggregatePercentage = calculatePercentage();

  // Check if aggregate marks are already set (for read-only display)
  const areMarksLocked = isEditing && form.obtainedMarks && form.maximumMarks;

  // Generate sorted country names
  const countryNames = Object.values(countries)
    .map(c => c.name)
    .sort();

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-3"></div>
          <p className="text-gray-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50/30 py-8 px-4">
      <div className="max-w-2xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="w-16 h-16 bg-blue-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-lg">
            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
            </svg>
          </div>
          <h1 className="text-3xl font-semibold text-gray-900 mb-2">
            {isEditing ? "Update Your Profile" : "Complete Your Profile"}
          </h1>
          <p className="text-gray-600 max-w-md mx-auto">
            {isEditing 
              ? "Update your profile information to refine your medical college recommendations"
              : "Help us provide personalized medical college recommendations by completing your profile"
            }
          </p>
        </div>

        {/* Progress Bar */}
        <div className="bg-white rounded-lg border border-gray-200 p-4 mb-6">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-medium text-gray-700">Profile Completion</span>
            <span className="text-sm font-bold text-blue-600">{completionPercentage}%</span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-2">
            <div 
              className="bg-blue-600 h-2 rounded-full transition-all duration-300"
              style={{ width: `${completionPercentage}%` }}
            ></div>
          </div>
          <p className="text-xs text-gray-500 mt-2">
            {completionPercentage === 100 ? "Ready to submit!" : `${8 - Object.values(form).filter(v => v !== "").length} fields remaining`}
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-lg border border-gray-200 p-8">
          <div className="space-y-6">
            {/* Country of Graduation */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Country of Medical Graduation
              </label>
              <select 
                name="country" 
                value={form.country} 
                onChange={handleChange} 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">Select your country</option>
                {countryNames.map(name => (
                  <option key={name} value={name}>{name}</option>
                ))}
              </select>
            </div>

            {/* Aggregate Marks of 4 Years - Read-only when editing and marks are set */}
            <div className={`p-4 rounded-lg border ${areMarksLocked ? "bg-gray-50 border-gray-200" : "bg-gray-50 border-gray-200"}`}>
              <label className="block text-sm font-semibold text-gray-700 mb-3">
                Aggregate Marks of 4 Years
                {areMarksLocked && (
                  <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                    Cannot be modified
                  </span>
                )}
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Obtained Marks
                  </label>
                  <input
                    type="number"
                    name="obtainedMarks"
                    value={form.obtainedMarks}
                    onChange={handleChange}
                    placeholder="e.g., 3250"
                    min="0"
                    step="0.01"
                    required
                    disabled={areMarksLocked}
                    className={`w-full px-3 py-2 border rounded-lg transition-colors ${
                      areMarksLocked
                        ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-600 mb-1">
                    Maximum Marks
                  </label>
                  <input
                    type="number"
                    name="maximumMarks"
                    value={form.maximumMarks}
                    onChange={handleChange}
                    placeholder="e.g., 4000"
                    min="1"
                    step="0.01"
                    required
                    disabled={areMarksLocked}
                    className={`w-full px-3 py-2 border rounded-lg transition-colors ${
                      areMarksLocked
                        ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                        : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    }`}
                  />
                </div>
              </div>
              {aggregatePercentage && (
                <div className="mt-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="flex items-center">
                    <svg className="w-4 h-4 text-blue-600 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                    </svg>
                    <span className="text-sm font-semibold text-blue-800">
                      Calculated Percentage: {aggregatePercentage}%
                    </span>
                  </div>
                </div>
              )}
              <p className="text-xs text-gray-500 mt-2">
                {areMarksLocked 
                  ? "Aggregate marks cannot be changed once set for data integrity"
                  : "Enter your total marks obtained and maximum possible marks across all 4 years of medical school"
                }
              </p>
            </div>

            {/* Time from Graduation */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Time from Graduation to Application
              </label>
              <select 
                name="graduationTime" 
                value={form.graduationTime} 
                onChange={handleChange} 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">Select time range</option>
                <option value="<1 year">Less than 1 year</option>
                <option value="1–2 years">1–2 years</option>
                <option value="3–5 years">3–5 years</option>
                <option value=">5 years">More than 5 years</option>
              </select>
            </div>

            {/* USMLE Step 1 - Read-only when editing */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                USMLE Step 1 Status
                {isEditing && form.step1 && (
                  <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                    Cannot be modified
                  </span>
                )}
              </label>
              <select 
                name="step1" 
                value={form.step1} 
                onChange={handleChange} 
                required
                disabled={isEditing && form.step1}
                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                  isEditing && form.step1
                    ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                }`}
              >
                <option value="">Select your Step 1 status</option>
                <option value="Pass in 1st attempt">Pass in 1st attempt</option>
                <option value="Pass in 2nd attempt">Pass in 2nd attempt</option>
                <option value="Pass in 3rd attempt">Pass in 3rd attempt</option>
                <option value="Fail">Fail</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {isEditing && form.step1 
                  ? "USMLE Step 1 status cannot be changed once set for data integrity"
                  : "Select your Step 1 attempt status"
                }
              </p>
            </div>

            {/* USMLE Step 2 - Read-only when editing */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                USMLE Step 2 CK Score
                {isEditing && form.step2 && (
                  <span className="ml-2 text-xs text-amber-600 bg-amber-100 px-2 py-1 rounded-full">
                    Cannot be modified
                  </span>
                )}
              </label>
              <select 
                name="step2" 
                value={form.step2} 
                onChange={handleChange} 
                required
                disabled={isEditing && form.step2}
                className={`w-full px-4 py-3 border rounded-lg transition-colors ${
                  isEditing && form.step2
                    ? "border-gray-200 bg-gray-50 text-gray-500 cursor-not-allowed"
                    : "border-gray-300 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                }`}
              >
                <option value="">Select your score range</option>
                <option value="Not passed">Not passed</option>
                {renderScoreRanges(209, 299)}
              </select>
              <p className="text-xs text-gray-500 mt-1">
                {isEditing && form.step2 
                  ? "USMLE Step 2 score cannot be changed once set for data integrity"
                  : "Select your score range"
                }
              </p>
            </div>

            {/* US Clinical Experience */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                U.S. Clinical Experience (USCE)
              </label>
              <select 
                name="usce" 
                value={form.usce} 
                onChange={handleChange} 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">Select experience level</option>
                <option value="None">None</option>
                <option value="<1 month">Less than 1 month</option>
                <option value="1–3 months">1–3 months</option>
                <option value="3+ months">3+ months</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Include rotations, observerships, and clinical work</p>
            </div>

            {/* Visa Sponsorship */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Visa Sponsorship Requirements
              </label>
              <select 
                name="visa" 
                value={form.visa} 
                onChange={handleChange} 
                required
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors"
              >
                <option value="">Select visa requirement</option>
                <option value="No need">No sponsorship needed</option>
                <option value="J1">J1 visa only</option>
                <option value="H1">H1B visa only</option>
                <option value="J1 or H1">J1 or H1B visa</option>
              </select>
              <p className="text-xs text-gray-500 mt-1">Your visa sponsorship needs for residency</p>
            </div>
          </div>

          {/* Message */}
          {message && (
            <div className={`mt-6 p-4 rounded-lg border ${
              message.includes("✅") 
                ? "bg-green-50 text-green-800 border-green-200" 
                : "bg-red-50 text-red-800 border-red-200"
            }`}>
              {message}
            </div>
          )}

          {/* Submit Button */}
          <div className="mt-8 flex flex-col sm:flex-row gap-4">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="flex-1 bg-white border border-gray-300 text-gray-700 hover:bg-gray-50 px-6 py-3 rounded-lg font-medium transition-colors"
            >
              {isEditing ? "Cancel" : "Skip for Now"}
            </button>
            <button
              type="submit"
              disabled={isSubmitting || completionPercentage < 100}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-400 text-white px-6 py-3 rounded-lg font-medium transition-colors disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {isEditing ? "Updating Profile..." : "Saving Profile..."}
                </>
              ) : (
                completionPercentage === 100 
                  ? (isEditing ? "Update Profile" : "Complete Profile")
                  : `${isEditing ? "Update" : "Complete"} Profile (${completionPercentage}%)`
              )}
            </button>
          </div>
        </form>

        {/* Help Section */}
        <div className="mt-6 bg-blue-50 rounded-lg p-4 border border-blue-200">
          <div className="flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-blue-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <div className="ml-3">
              <p className="text-sm text-blue-800">
                <strong>Need help?</strong> This information helps us provide personalized college recommendations. 
                {isEditing 
                  ? " Note: USMLE Step 1, Step 2 scores, and aggregate marks cannot be modified once set to maintain data integrity."
                  : " You can update most details anytime from your profile settings."
                }
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SetupProfile;